/**
 * The full desktop menu (Sidebar.tsx), built in one place so every frame that
 * draws it — the desktop sidebar and the phone's More drawer — offers the same
 * pages behind the same module, flag, space and permission gates.
 *
 * Moved out of Sidebar.tsx unchanged (2026-11-02, phone audit #33): the phone
 * frame reached 27 pages while the desktop sidebar reached about 90, because
 * the phone kept its own hand-made list.
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Inbox,
  RefreshCcw,
  Target,
  Settings,
  PlusCircle,
  FileText,
  Layers,
  Swords,
  Library,
  AlertTriangle,
  BarChart3,
  GitBranch,
  Wrench,
  ArrowDown,
  ClipboardList,
  Bell,
  TreeDeciduous,
  Lightbulb,
  ListChecks,
  Flag,
  Network,
  CornerDownRight,
  GitFork,
  Link2,
  Clock,
  Search,
  Calendar,
  Trophy,
  LayoutGrid,
  CheckSquare,
  CheckCircle2,
  Compass,
  PlayCircle,
  ScrollText,
  BadgeCheck,
  ArrowDownUp,
  UserCircle,
  LayoutDashboard,
  Radio,
  Handshake,
  Map,
  HelpCircle,
  LayoutTemplate,
  AlignVerticalJustifyStart,
  CircleDot,
  Milestone,
  ListOrdered,
  Telescope,
  Crosshair,
  Gavel,
  Package,
  Group,
  Tags,
  Brain,
  TrendingUp,
  ShieldAlert,
  Scale,
  Camera,
} from 'lucide-react';

import { useAttentionCounts } from '../../hooks/prioritization/useAttentionCounts';
import { useWorkflowStore } from '../../stores/prioritization/workflowStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useMyTasksNavQueueMetrics } from '../../hooks/useMyTasksNavQueueMetrics';
import { useNeedsYouCount } from '../../hooks/useNeedsYouCount';
import { FEATURE_FLAGS, FEATURE_FLAGS_UPDATED_EVENT } from '../../lib/featureFlags';
import { useModuleGate } from '../../lib/moduleGate';
import { useAppArea, type AppArea } from '../../lib/appAreas';
import { useAreaReports } from '../../hooks/prioritization/useAreaReports';
import { actionMenu, plansMenu, HELP_MENU, type AreaMenuItem } from '../../lib/areaMenus';
import { useTimePhasedPlanningEnabled } from '../../features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';
import { getWorkspaceModules, isModuleEnabled, WORKSPACE_MODULES_UPDATED_EVENT, isModuleFeatureAccessible } from '../../lib/workspaceModulesService';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import {
  roleCanAccessNavSection,
  roleCanAccessNavItem,
  toActorRole,
  type NavSectionId,
} from '../../types/prioritization/rolePermissionMapping';
import { useAccessibleApps } from '../../apps/RequireAppAccess';
import { usePinnedCycleStore } from '../../stores/prioritization/pinnedCycleStore';
import {
  PERSONAL_GROUP,
  WORKSPACE_GROUPS,
  filterVisibleSettingsItems,
  hasAnyAccessibleSettings,
  isSettingsGroupVisibleInSidebar,
} from '../../pages/prioritization/settings/settingsConfig';
import type {
  SettingsAccessContext,
  SettingsItem,
} from '../../pages/prioritization/settings/settingsConfig';

import { useSpaceKind, isSmallSpaceKind } from '../../hooks/prioritization/useSpaceKind';




export interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  /** Optional react-router location.state on `<Link>` (e.g. return context from Prioritization into Planning routes). */
  linkState?: unknown;
  isDivider?: boolean;       // renders as a section label, not a link
  dividerLabel?: string;     // label text for the divider
  /** Visually indented beneath the preceding item — no expand/collapse */
  isSubItem?: boolean;
  /** Optional nested items — renders this item as an expandable group header */
  subItems?: NavItem[];
}

export interface NavSection {
  name: string;
  sectionId: NavSectionId;
  icon: React.ElementType;
  items?: NavItem[];
  href?: string;
  defaultOpen?: boolean;
  collapsed?: boolean;
  badge?: number;
  /** Optional group label (e.g. "Item Management") — renders as small uppercase label above section when present */
  groupLabel?: string;
  /**
   * If set, the section header highlights only when the current path starts with
   * one of these prefixes (and does NOT start with any activePathExclusions).
   * Prevents two sections sharing the same child hrefs from both lighting up.
   */
  activePaths?: string[];
  activePathExclusions?: string[];
  /**
   * When set, clicking the section header navigates here AND toggles expansion.
   * Used for settings groups whose header links to their own index page while
   * still showing expandable children below.
   */
  headerHref?: string;
  /**
   * When true, renders as a non-clickable label heading with items shown flat
   * at the first level — no collapsible, no indentation.
   */
  flatSection?: boolean;
}

export const CATCHBALL_HREF = '/prioritization/catchball';

/** What the areas' menus are built from, besides the permission-filtered sections. */
export interface AreaNavigationInputs {
  /** The Reports menu for the area (useAreaReports). */
  reports: AreaMenuItem[];
  timePhasedEnabled: boolean;
  evidenceEnabled: boolean;
}

/**
 * The sidebar for one area (src/lib/appAreas.ts): the shared entries above
 * (Home, Apps, Notifications, Tasks, Search), the area's own menu, then
 * Reports, Decisions and Settings, then Help. Pure, so the desktop sidebar and
 * the phone's More drawer draw the same menu from the same sections.
 */
export function composeAreaNavigation(
  navigation: NavSection[],
  area: AppArea,
  { reports, timePhasedEnabled, evidenceEnabled }: AreaNavigationInputs,
): NavSection[] {
  const TOP = new Set<string>(['home', 'my-tasks']);
  const BOTTOM = new Set<string>(['monitor', 'decisions', 'settings']);
  const AREA_SECTIONS: Record<'strategy' | 'priorities', Set<string>> = {
    strategy: new Set(['strategy']),
    priorities: new Set(['intake', 'items', 'relationships', 'cycle-execution', 'tools']),
  };
  const top = navigation.filter((sec) => TOP.has(sec.sectionId));
  const bottom = navigation
    .filter((sec) => BOTTOM.has(sec.sectionId))
    .map((sec) =>
      sec.name === 'Reports'
        ? { ...sec, items: reports.map((it) => ({ name: it.name, href: it.href, icon: it.icon, ...(it.isSubItem ? { isSubItem: true } : {}) })) }
        : sec,
    );
  let middle: NavSection[];
  if (area === 'strategy' || area === 'priorities') {
    middle = navigation.filter((sec) => AREA_SECTIONS[area].has(sec.sectionId));
    // Strategy: the Strategies section as it was, then Roadmaps, Back Plans and Open Questions as their
    // own top-level entries (owner ruling 2026-10-15).
    if (area === 'strategy') {
      middle = [
        ...middle,
        { name: 'Roadmaps', sectionId: 'strategy' as NavSectionId, href: '/prioritization/roadmap', icon: Milestone, groupLabel: 'Strategy' },
        { name: 'Back Plans', sectionId: 'strategy' as NavSectionId, href: '/prioritization/backplan', icon: Map, groupLabel: 'Strategy' },
        { name: 'Open Questions', sectionId: 'strategy' as NavSectionId, href: '/prioritization/open-questions', icon: HelpCircle, groupLabel: 'Strategy' },
      ];
    }
  } else {
    const menu = area === 'plans' ? plansMenu({ timePhasedEnabled }) : actionMenu({ evidenceEnabled });
    middle = menu.map((m) => ({
      name: m.label,
      sectionId: 'monitor' as NavSectionId,
      icon: m.items[0]?.icon ?? LayoutDashboard,
      flatSection: true,
      items: m.items.map((it) => ({ name: it.name, href: it.href, icon: it.icon })),
    }));
  }
  // Help (the Game, the wristband) is for everyone, so it skips the permission filter above.
  const help: NavSection[] = HELP_MENU.items.map((it) => ({
    name: it.name,
    sectionId: 'home' as NavSectionId,
    href: it.href,
    icon: it.icon,
    groupLabel: HELP_MENU.label,
  }));
  return [...top, ...middle, ...bottom, ...help];
}

/**
 * The permission-filtered sidebar sections (`navigation`), every section before
 * filtering (`allNavigation`), and the menu for the current area
 * (`areaNavigation`, what the desktop sidebar draws).
 */
export function useSidebarNavigation() {
  const location = useLocation();
  // App Layer entry point. Resolved the same way /apps resolves it, so the link
  // is absent for anyone with no apps rather than dead-ending them (C Mode rule 2).
  const { apps: accessibleApps } = useAccessibleApps();
  const workflows = useWorkflowStore((state) => state.workflows);
  const fetchWorkflows = useWorkflowStore((state) => state.fetchWorkflows);
  const currentRole = useUserStore((state) => state.currentRole);
  const assignedRoleNames = useUserStore((state) => state.assignedRoleNames);
  const permissionsLoaded = useUserStore((state) => state.permissionsLoaded);
  const canAny = useUserStore((state) => state.canAny);
  const userId = useUserStore((state) => state.userId);
  const [featureFlagsVersion, setFeatureFlagsVersion] = useState(0);
  const [workspaceFeatures, setWorkspaceFeatures] = useState<Record<string, boolean>>({});
  // The current space's type, else the workspace's type (shared with the role-based menu).
  const spaceKind = useSpaceKind();
  const isOnSettingsRoute =
    location.pathname.startsWith('/prioritization/settings') ||
    location.pathname.startsWith('/prioritization/admin');

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  // Load workspace module state directly from workspaces.features. Module
  // visibility (Strategy, Strategy Kernel, Planning, etc.) is driven by the
  // DB — not mirrored into localStorage feature flags — so the sidebar
  // reflects the server-enforced source of truth.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) {
        if (!cancelled) setWorkspaceFeatures({});
        return;
      }
      getWorkspaceModules(workspaceId).then(({ data }) => {
        if (!cancelled) setWorkspaceFeatures(data);
      });
    };
    load();
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, load);
    };
  }, []);

  useEffect(() => {
    const refreshFeatureFlags = () => setFeatureFlagsVersion((version) => version + 1);
    window.addEventListener(FEATURE_FLAGS_UPDATED_EVENT, refreshFeatureFlags);
    window.addEventListener('storage', refreshFeatureFlags);
    return () => {
      window.removeEventListener(FEATURE_FLAGS_UPDATED_EVENT, refreshFeatureFlags);
      window.removeEventListener('storage', refreshFeatureFlags);
    };
  }, []);

  const showCatchballNav = useMemo(
    () => isModuleEnabled(workspaceFeatures, 'catchball') && workflows.some((w) => (w.configuration as { catchball_enabled?: boolean } | undefined)?.catchball_enabled !== false),
    [workspaceFeatures, workflows]
  );

  // The small-space menu: in a personal or team space the full menu leaves out
  // Strategy, Relationships and Reports (it was called "simplified mode" before
  // 2026-10-14; one rule, `isSmallSpaceKind` in useSpaceKind).
  // The space type (space-level) takes precedence over the workspace type
  // so that switching spaces instantly updates the sidebar without a workspace reload.
  // Not the same idea as the role-based member menu (ContributorSidebar, module
  // contributor_role_nav): this one trims the FULL menu by the kind of space;
  // that one replaces the full menu with a short one by the person's roles.
  const isSmallSpace = isSmallSpaceKind(spaceKind);

  const showStrategyNav = useMemo(
    // Every space, personal included: the Strategy area is where it appears (owner ruling 2026-10-15).
    () => isModuleFeatureAccessible(workspaceFeatures, 'strategy', FEATURE_FLAGS.STRATEGY_NAV),
    // featureFlagsVersion is intentionally included to force re-eval when flags change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion, isSmallSpace]
  );
  const showStrategyKernelNav = useMemo(
    () => !isSmallSpace && isModuleFeatureAccessible(workspaceFeatures, 'strategy_kernel', FEATURE_FLAGS.STRATEGY_KERNEL_NAV),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion, isSmallSpace]
  );
  const showCommitmentRegistryNav = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'commitment_registry', FEATURE_FLAGS.COMMITMENT_REGISTRY),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion]
  );
  const showStakeholderPulseNav = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'stakeholder_pulse', FEATURE_FLAGS.STAKEHOLDER_PULSE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion]
  );
  const showStrategyCanvasNav = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'strategy_canvas', FEATURE_FLAGS.STRATEGY_CANVAS_NAV),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion]
  );
  const showV2momNav = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'v2mom', FEATURE_FLAGS.V2MOM_NAV),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion]
  );
  const showJudgmentIndex = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'judgment_index', FEATURE_FLAGS.JUDGMENT_INDEX),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion]
  );
  const showJudgmentAnalytics = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'judgment_analytics', FEATURE_FLAGS.JUDGMENT_ANALYTICS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion]
  );
  const showDeliveryTracking = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'delivery_tracking', FEATURE_FLAGS.DELIVERY_TRACKING),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceFeatures, featureFlagsVersion]
  );
  // Decision Log (/brief) — on by default; a workspace admin can switch it off (owner ruling 2026-10-14).
  const showDecisionBrief = useModuleGate('decision_brief');
  // Everything under Decisions can be switched off on its own (owner ask 2026-10-14).
  const showDecisionIntel = useMemo(() => isModuleEnabled(workspaceFeatures, 'decision_intelligence'), [workspaceFeatures]);
  const showBiasAudit = useMemo(() => isModuleEnabled(workspaceFeatures, 'bias_audit'), [workspaceFeatures]);
  const showCalibration = useMemo(() => isModuleEnabled(workspaceFeatures, 'decision_calibration'), [workspaceFeatures]);

  // Extended Navigation module — gates Scenario Planner, Executive Inbox, Proposals,
  // Ratification, and Commitments. (Input rounds has its own module, `input_rounds`.)
  const showExtendedNav = useMemo(
    () => isModuleEnabled(workspaceFeatures, 'extended_navigation'),
    [workspaceFeatures]
  );

  // Standalone governance nav items (Executive Inbox, Proposals, Ratification) are
  // hidden when the workspace disables this module — e.g. demo workspaces where all
  // governance flows through the Catchball subnav directly.
  const showStandaloneGovernanceNav = useMemo(
    () => isModuleEnabled(workspaceFeatures, 'standalone_governance_nav'),
    [workspaceFeatures]
  );

  const showTournamentsNav = useModuleGate('tournaments');

  const showReportsNav = useMemo(
    () => isModuleEnabled(workspaceFeatures, 'reports'),
    [workspaceFeatures]
  );

  // User can access a section/item if currentRole can OR any assigned role can (so e.g. Shape shows for workspace-admin even when display role is strategist)
  const userCanAccessSection = useCallback(
    (sectionId: NavSectionId) => {
      if (currentRole && roleCanAccessNavSection(currentRole, sectionId)) return true;
      const roles = assignedRoleNames ?? [];
      return roles.some((r: string) => {
        const actorRole = toActorRole(r);
        return actorRole ? roleCanAccessNavSection(actorRole, sectionId) : false;
      });
    },
    [currentRole, assignedRoleNames]
  );
  const userCanAccessNavItem = useCallback(
    (href: string) => {
      if (currentRole && roleCanAccessNavItem(currentRole, href)) return true;
      const roles = assignedRoleNames ?? [];
      return roles.some((r: string) => {
        const actorRole = toActorRole(r);
        return actorRole ? roleCanAccessNavItem(actorRole, href) : false;
      });
    },
    [currentRole, assignedRoleNames]
  );

  // Settings visibility is driven by per-item `requiredPermissions` declared in
  // settingsConfig. We check the user's actual permissions (capability-resolved
  // via userStore.canAny) instead of the coarse NAV_SECTION_PERMISSIONS bucket,
  // so a regular user still sees "My Account" even when they have zero admin
  // permissions, and an admin with only e.g. admin.users sees only the pages
  // they can actually act on.
  const isPlatformAdminCurrent =
    currentRole === 'platform-admin' || currentRole === 'system-admin';
  const settingsAccessCtx = useMemo<SettingsAccessContext>(
    () => ({
      canAny,
      isPlatformAdmin: isPlatformAdminCurrent,
      isModuleEnabled: (moduleKey: string) => isModuleEnabled(workspaceFeatures, moduleKey),
    }),
    [canAny, isPlatformAdminCurrent, workspaceFeatures],
  );
  const accessibleSettings = useMemo(() => {
    const toNavItems = (items: SettingsItem[]) =>
      filterVisibleSettingsItems(items, settingsAccessCtx).map((i) => ({
        name: i.name,
        href: i.href,
        icon: i.icon,
      }));
    const isTeamSpace = spaceKind === 'team';
    const sidebarGroupOpts = { isSmallSpace, isTeamSpace };
    const workspaceGroups = WORKSPACE_GROUPS.map((group) => ({
      group,
      items: toNavItems(group.items),
    }))
      .filter(({ group, items }) =>
        items.length > 0 && isSettingsGroupVisibleInSidebar(group, sidebarGroupOpts),
      );
    return {
      hasAny: hasAnyAccessibleSettings(settingsAccessCtx),
      personal: toNavItems(PERSONAL_GROUP.items),
      workspaceGroups,
    };
  }, [settingsAccessCtx, isSmallSpace, spaceKind]);

  const { triageCount } = useAttentionCounts();

  const pinnedCycleId = usePinnedCycleStore(state => state.pinnedCycleId);
  // Toggle to show/hide restricted items (for debugging)
  const showRestricted = false; // Debug "Show Restricted Items" UI removed

  // My work's badge: what needs the person now, the engine's count (the same number
  // Home, My work and the phone badge show).
  const needsYouCount = useNeedsYouCount(userId, getCurrentWorkspaceId());
  const tasksNavBadge = needsYouCount > 0 ? needsYouCount : undefined;
  const tasksNavMetrics = useMyTasksNavQueueMetrics(userId, getCurrentWorkspaceId());
  // Catchball nav badge: active turns where the ball is in the user's court (not hidden).
  const catchballNavBadge =
    tasksNavMetrics.visibleCatchballCount > 0 ? tasksNavMetrics.visibleCatchballCount : undefined;

  // v3.0 - Navigation oriented by operating domains, not a mandatory sequence.
  const allNavigation: NavSection[] = [
    // --- Awareness & responsibilities ---
    {
      name: 'Home',
      sectionId: 'home' as NavSectionId,
      href: '/prioritization/dashboard',
      icon: LayoutDashboard,
    },
    // Sits under Dashboard because the Apps widget on the dashboard is the other
    // half of this doorway — the widget shows what is there, this gets you back.
    ...(accessibleApps.length > 0
      ? [
          {
            name: 'Apps',
            sectionId: 'home' as NavSectionId,
            // In-shell directory, not the standalone consumer entry at /apps —
            // clicking a nav item must never drop the nav.
            href: '/prioritization/apps',
            icon: LayoutGrid,
          },
        ]
      : []),
    // The full notification list. Its count stays on the header bell, which
    // owns the badge rules (notificationBadgeRules.ts); a second count here
    // would be a second computation of the same number.
    {
      name: 'Notifications',
      sectionId: 'home' as NavSectionId,
      href: '/prioritization/notifications',
      icon: Bell,
    },
    {
      name: 'My work',
      sectionId: 'my-tasks',
      href: '/prioritization/my-work',
      icon: ClipboardList,
      badge: tasksNavBadge,
    },
    {
      name: 'Search',
      sectionId: 'home' as NavSectionId,
      href: '/prioritization/search',
      icon: Search,
    },
    // --- Strategy (frameworks + clarity artifacts + classic alignment) ---
    {
      name: 'Strategies',
      sectionId: 'strategy',
      icon: Telescope,
      groupLabel: 'Strategy',
      items: [
        { name: 'Strategies', href: '/prioritization/strategy', icon: TreeDeciduous },
        { name: 'Guided setup', href: '/prioritization/strategy/guided-setup', icon: Compass },
        ...(showStrategyCanvasNav
          ? [{ name: 'Strategy Canvas', href: '/prioritization/strategy/canvas', icon: LayoutTemplate }]
          : []),
        ...(showV2momNav
          ? [{ name: 'V2MOM', href: '/prioritization/strategy/v2mom', icon: AlignVerticalJustifyStart }]
          : []),
        { name: 'Outcome Drivers', href: '/prioritization/outcome-drivers', icon: CircleDot },
        { name: 'Desired Outcomes', href: '/prioritization/desired-outcomes', icon: CheckSquare },
        { name: 'Objectives', href: '/prioritization/strategy/objectives', icon: Crosshair },
        { name: 'Goals', href: '/prioritization/strategy/goals', icon: Flag },
        { name: 'OKRs', href: '/prioritization/strategy/okrs', icon: BarChart3 },
        { name: 'Strategy Kernel', href: '/prioritization/strategy/kernel', icon: Lightbulb },
      ],
    },
    // --- Item Management (always-on) ---
    {
      name: 'Intake',
      sectionId: 'intake',
      icon: Inbox,
      groupLabel: 'Item Management',
      items: [
        { name: 'Submit an idea', href: '/prioritization/intake/submit', icon: PlusCircle },
        { name: 'My Submissions', href: '/prioritization/intake/mine', icon: FileText },
        { name: 'Triage Queue', href: '/prioritization/intake/triage', icon: AlertTriangle, badge: triageCount > 0 ? triageCount : undefined },
        ...(showStakeholderPulseNav ? [{ name: 'Stakeholder Pulse', href: '/prioritization/pulse', icon: Radio }] : []),
      ],
    },
    {
      name: 'Items',
      sectionId: 'items',
      icon: Library,
      items: [
        { name: 'All Items', href: '/prioritization/items', icon: Library },
        { name: 'Item Pools', href: '/prioritization/lists', icon: ListChecks },
        { name: 'Completeness', href: '/prioritization/items/readiness', icon: CheckSquare },
        { name: 'Comparison Grid', href: '/prioritization/items/types', icon: LayoutGrid },
        { name: 'Whiteboards', href: '/prioritization/items/whiteboards', icon: Camera },
        { name: 'Values', href: '/prioritization/scoring', icon: BarChart3 },
        { name: 'Graph', href: '/prioritization/shape/relationships', icon: GitBranch },
        { name: 'Lineage', href: '/prioritization/lineage', icon: GitBranch },
        ...(showExtendedNav ? [{ name: 'Scenario Planner', href: '/prioritization/tools/relationship-scenario-planner', icon: ArrowDownUp }] : []),
      ],
    },
    ...(!isSmallSpace ? [{
      name: 'Relationships',
      sectionId: 'relationships' as NavSectionId,
      icon: Network,
      headerHref: '/prioritization/relationships',
      items: [
        { name: 'Collections',  href: '/prioritization/collections',                          icon: Link2       },
        { name: 'Sequence',     href: '/prioritization/relationships/sequence',                icon: GitBranch   },
        { name: 'Composition',  href: '/prioritization/relationships/composition',             icon: ArrowDown   },
        { name: 'Contains',     href: '/prioritization/relationships/contains',                icon: CornerDownRight },
        { name: 'Elaborations', href: '/prioritization/relationships/elaborations',            icon: GitFork     },
        { name: 'Additional Categories', href: '/prioritization/relationships/reframes',               icon: Tags        },
        { name: 'Clusters',     href: '/prioritization/relationships/clusters',               icon: Group       },
        { name: 'Packages',     href: '/prioritization/relationships/packages',               icon: Package     },
        { name: 'Competing',    href: '/prioritization/relationships/exclusives',             icon: Swords      },
        { name: 'Lineage',      href: '/prioritization/relationships/lineage',                icon: Clock       },
      ],
    } as NavSection] : []),
    // --- Cycles (flat label — no collapsible; workflows/templates: Settings → Content & Structure) ---
    {
      name: 'Cycles',
      sectionId: 'cycle-execution' as NavSectionId,
      icon: PlayCircle,
      flatSection: true,
      activePaths: [
        '/prioritization/cycle-design',
        '/prioritization/cycles',
        '/prioritization/tracks',
        '/prioritization/sessions',
        '/prioritization/reports/sessions',
        '/prioritization/scoreboard',
        '/prioritization/readiness',
        '/prioritization/tournaments',
        '/prioritization/catchball',
        '/prioritization/governance',
        '/prioritization/commitments',
      ],
      items: isSmallSpace
        // Small space: just the work surfaces (sessions + results). No governance chrome.
        ? [
            { name: 'Events & Sessions', href: '/prioritization/sessions',    icon: Target },
            { name: 'Scoreboard',     href: '/prioritization/scoreboard',  icon: Trophy },
            ...(showTournamentsNav ? [{ name: 'Tournaments', href: '/prioritization/tournaments', icon: GitBranch }] : []),
          ]
        : [
            // Design Hub (/prioritization/cycle-design) left the menu 2026-11-03 (owner
            // ruling); the page stays reachable by its address until it is retired.
            ...(pinnedCycleId
              ? [
                  {
                    name: 'Workspace',
                    href: `/prioritization/cycles/${pinnedCycleId}?tab=tracks`,
                    icon: LayoutDashboard,
                  },
                  {
                    name: 'Active Cycle',
                    href: `/prioritization/cycles/${pinnedCycleId}`,
                    icon: Calendar,
                  },
                  {
                    name: 'Cycles',
                    href: '/prioritization/cycles',
                    icon: Calendar,
                  },
                ]
              : [
                  {
                    name: 'Cycles',
                    href: '/prioritization/cycles',
                    icon: Calendar,
                  },
                ]),
            {
              name: 'Tracks',
              href: pinnedCycleId
                ? `/prioritization/tracks?cycleId=${pinnedCycleId}`
                : '/prioritization/tracks',
              icon: Layers,
            },
            {
              name: 'Events & Sessions',
              href: pinnedCycleId
                ? `/prioritization/sessions?cycleId=${pinnedCycleId}`
                : '/prioritization/sessions',
              icon: Target,
            },
            ...(showReportsNav ? [{ name: 'Session Results', href: '/prioritization/reports/sessions', icon: ListOrdered }] : []),
            { name: 'Scoreboard', href: '/prioritization/scoreboard', icon: Trophy },
            ...(showTournamentsNav ? [{ name: 'Tournaments', href: '/prioritization/tournaments', icon: GitBranch }] : []),
            { name: 'Readiness', href: '/prioritization/readiness', icon: CheckCircle2 },
            ...(showCatchballNav ? [{
              name: 'Cascade',
              href: pinnedCycleId
                ? `/prioritization/cycles/${pinnedCycleId}?tab=exchange`
                : '/prioritization/catchball',
              icon: RefreshCcw,
              badge: catchballNavBadge,
            }] : []),
            ...(showExtendedNav && showStandaloneGovernanceNav ? [{
              name: 'Executive Inbox',
              href: '/prioritization/cascade/executive-inbox',
              icon: Inbox,
            }] : []),
            ...(showExtendedNav && showStandaloneGovernanceNav ? [{
              name: 'Proposals',
              href: pinnedCycleId
                ? `/prioritization/governance/proposals?cycleId=${pinnedCycleId}`
                : '/prioritization/governance/proposals',
              icon: ScrollText,
            }] : []),
            ...(showExtendedNav && showStandaloneGovernanceNav ? [{
              name: 'Ratification',
              href: pinnedCycleId
                ? `/prioritization/governance/ratification?cycleId=${pinnedCycleId}`
                : '/prioritization/governance/ratification',
              icon: BadgeCheck,
            }] : []),
            ...(showExtendedNav && showCommitmentRegistryNav
              ? [{ name: 'Commitments', href: '/prioritization/commitments', icon: Handshake }]
              : []),
          ],
    },
    // --- Tool Mode — always visible, it's the core feature ---
    {
      name: 'Tools',
      sectionId: 'tools' as NavSectionId,
      icon: Wrench,
      groupLabel: 'Tools',
      href: '/prioritization/tools',
    },
    // --- Reports --- (and Decisions, same gate) on every page and in every space (owner ruling 2026-10-15)
    ...(showReportsNav ? [{
      name: 'Reports',
      sectionId: 'monitor' as NavSectionId,
      icon: BarChart3,
      groupLabel: 'Reports',
      items: [
        // Filled per area by useAreaReports (the reports placed in that area).
        { name: 'All Reports',          href: '/prioritization/reports',                            icon: BarChart3  },
        { name: 'Custom Reports',       href: '/prioritization/reports/custom',                    icon: FileText   },
      ],
    } as NavSection,
    // --- Decisions — what was decided and how well (roadmap N-18: out of Reports).
    // Same gate as Reports so nobody gains or loses access in the move.
    {
      name: 'Decisions',
      sectionId: 'decisions' as NavSectionId,
      icon: GitBranch,
      groupLabel: 'Decisions',
      items: [
        ...(showExtendedNav ? [{ name: 'Decisions', href: '/prioritization/decisions', icon: Gavel }] : []),
        // The Decision Log (/brief) is a chrome-free surface; this in-shell
        // bridge page links into it without dropping the nav. Hidden while the
        // decision_brief module is off (the default).
        ...(showDecisionBrief ? [{ name: 'Decision Log', href: '/prioritization/decisions/brief', icon: Gavel, isSubItem: true }] : []),
        ...(showExtendedNav && showDecisionIntel ? [{ name: 'Decision Intel', href: '/prioritization/decision-intelligence', icon: Brain }] : []),
        ...(showDeliveryTracking && showCalibration ? [{ name: 'Calibration', href: '/prioritization/calibration', icon: TrendingUp }] : []),
        ...(showBiasAudit ? [{ name: 'Bias Audit', href: '/prioritization/insights/cognitive-bias', icon: ShieldAlert }] : []),
        ...(showJudgmentIndex ? [{ name: 'Judgment Index', href: '/prioritization/insights/judgment-index', icon: Scale }] : []),
        ...(showJudgmentAnalytics ? [{ name: 'Judgment Analytics', href: '/prioritization/insights/judgment-analytics', icon: Compass }] : []),
      ],
    } as NavSection] : []),
    // Settings — "Settings" is a label; each group is its own collapsible section.
    // Each section's `items` have already been filtered by the user's permissions
    // via `accessibleSettings`. Sections with zero accessible items fall out
    // automatically in the downstream `filteredItems.length === 0` step.
    // The overall "Settings" label/overview link is only included when the user
    // has access to at least one settings page.
    ...(accessibleSettings.hasAny
      ? [
          {
            name: 'Settings',
            sectionId: 'settings' as NavSectionId,
            icon: Settings,
            groupLabel: 'Settings',
            href: '/prioritization/settings',
          },
        ]
      : []),
    // Settings subsections — only after entering Settings (not in main nav by default)
    ...(isOnSettingsRoute
      ? [
          {
            name: 'My Account',
            sectionId: 'settings',
            icon: UserCircle,
            headerHref: PERSONAL_GROUP.indexHref,
            items: accessibleSettings.personal,
          },
          ...accessibleSettings.workspaceGroups.map(({ group, items }) => ({
            name: group.title,
            sectionId: 'settings' as NavSectionId,
            icon: group.icon,
            headerHref: group.indexHref,
            items,
          } as NavSection)),
        ]
      : []),
  ];
  
    // When permissions aren't loaded yet, show all nav so nav is never empty
    const navigation = useMemo(() => {
    if (showRestricted) return allNavigation;

    const authStillHydrating = !permissionsLoaded || (!currentRole && (assignedRoleNames?.length ?? 0) === 0);
    const canAccessSection = authStillHydrating ? () => true : userCanAccessSection;
    const canAccessItem = authStillHydrating ? () => true : userCanAccessNavItem;

    const filteredNavigation = allNavigation
      // Settings sections handle their own per-item visibility via
      // `accessibleSettings`; the coarse NAV_SECTION_PERMISSIONS check would
      // incorrectly hide personal settings (My Account) from non-admins. For
      // every other section, apply the section-level permission check.
      .filter((section) => section.sectionId === 'settings' || canAccessSection(section.sectionId))
      .map((section) => {
        if (!section.items) return section;

        const filteredItems = section.items.filter(
          (item) =>
            item.isDivider ||
            // Settings items are already filtered by their per-item required
            // permissions in `accessibleSettings`; don't re-filter them with
            // the coarse NAV_ITEM_PERMISSIONS map (which only covers a handful
            // of paths and would fail-open for the rest).
            ((section.sectionId === 'settings' || canAccessItem(item.href)) &&
            (item.href !== CATCHBALL_HREF || showCatchballNav) &&
            (item.href !== '/prioritization/strategy/kernel' || showStrategyKernelNav) &&
            (item.href !== '/platform-admin' || currentRole === 'platform-admin' || currentRole === 'system-admin'))
        );

        if (filteredItems.length === 0) return null;

        return { ...section, items: filteredItems };
      })
      .filter((section): section is NavSection => section !== null)
      .filter((section) => section.sectionId !== 'strategy' || showStrategyNav)
      ;

    // Fail open: primary navigation should never disappear while auth/workspace state settles.
    // Also trigger when only settings sections are visible — that means content sections
    // were incorrectly filtered out (e.g. currentRole resolved to null during hydration).
    const hasNonSettingsSection = filteredNavigation.some((s) => s.sectionId !== 'settings');
    if (filteredNavigation.length === 0 || !hasNonSettingsSection) {
      return allNavigation
        .map((section) => {
          if (!section.items) return section;
          const visibleItems = section.items.filter(
            (item) =>
              item.isDivider ||
              ((item.href !== CATCHBALL_HREF || showCatchballNav) &&
              (item.href !== '/prioritization/strategy/kernel' || showStrategyKernelNav) &&
              (item.href !== '/platform-admin' || currentRole === 'platform-admin' || currentRole === 'system-admin'))
          );
          if (visibleItems.length === 0) return null;
          return { ...section, items: visibleItems };
        })
        .filter((section): section is NavSection => section !== null)
        .filter((section) => section.sectionId !== 'strategy' || showStrategyNav);
    }

    return filteredNavigation;
  // allNavigation is an inline array literal; adding it as a dep would recompute on every render.
  // The values it's built from ARE in the dep array below, so this is safe to suppress.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole, assignedRoleNames, showRestricted, showCatchballNav, showTournamentsNav, showReportsNav, showStrategyNav, showStrategyKernelNav, showStrategyCanvasNav, showV2momNav, showCommitmentRegistryNav, showExtendedNav, showStandaloneGovernanceNav, permissionsLoaded, userCanAccessSection, userCanAccessNavItem, accessibleSettings, isSmallSpace, spaceKind, isOnSettingsRoute, location.pathname]);
  
  // ── The four areas (src/lib/appAreas.ts): shared entries above and below, the area's own menu between ──
  const area = useAppArea(location.pathname);
  // The Reports menu lists the reports placed in this area (Settings → Reports).
  const areaReports = useAreaReports(area);
  const timePhasedEnabled = useTimePhasedPlanningEnabled();
  const evidenceEnabled = useModuleGate('evidence');
  const areaNavigation = useMemo(
    () => composeAreaNavigation(navigation, area, { reports: areaReports, timePhasedEnabled, evidenceEnabled }),
    [navigation, area, areaReports, timePhasedEnabled, evidenceEnabled],
  );

  return { allNavigation, navigation, area, areaNavigation, timePhasedEnabled, evidenceEnabled };
}
